import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./HomeHub.css";
import "./Home2.css";
import { useData } from "./Cache";
import { encodeSegment } from "./AltHomeUtils";
import { mapCityKeyOf, normalizeMapCityKey } from "./mapCityKey";
import Logo from "../assets/elitlig-beyaz.png";
import api from "../api/api";
import HomeLoginCard from "../components/home/HomeLoginCard";

// Harita SVG'si ayrı parçadan gelir (bkz. TurkeyMap.js); ana paket küçülür.
const TurkeyMap = lazy(() => import("./TurkeyMap"));


export default function Home2() {
  const navigate = useNavigate();
  const tooltipRef = useRef(null);
  const { cities = [], refetchMeta } = useData();
  const [fallbackDefaults, setFallbackDefaults] = useState({ leagueId: "", seasonId: "" });

  // Şehir kaydı → SVG yolundaki data-city-name (bkz. mapCityKey.js: İstanbul
  // iki yaka, eski "İstanbul" etiketi Avrupa yakası).
  const normalizeCityKey = useCallback((value) => normalizeMapCityKey(value), []);

  const cityIdMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(cities) ? cities : []).forEach((city) => {
      const labelKey = mapCityKeyOf(city);
      const id = String(city?.id || "").trim();
      if (labelKey && id) map.set(labelKey, id);
    });
    return map;
  }, [cities]);

  const citiesWithData = useMemo(() => {
    const set = new Set();
    (Array.isArray(cities) ? cities : []).forEach((city) => {
      const key = mapCityKeyOf(city);
      if (key) set.add(key);
    });
    return set;
  }, [cities]);

  const sortedCities = useMemo(() => {
    const list = (Array.isArray(cities) ? cities : []).map((city) => ({
      key: mapCityKeyOf(city),
      id: String(city?.id || "").trim(),
      label: city?.label || city?.name || city?.slug || "",
      priority: Number.isFinite(Number(city?.priority)) ? Number(city?.priority) : 999999,
      isDefault: Boolean(city?.is_default ?? city?.isDefault ?? city?.default),
    }));
    list.sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.label.localeCompare(b.label, "tr");
    });
    const fromMeta = list.filter((item) => item.key);
    if (fromMeta.length) return fromMeta;
    return Array.from(citiesWithData)
      .map((key) => ({ key, id: cityIdMap.get(key) || "", label: key, priority: 999999, isDefault: false }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [cities, citiesWithData, cityIdMap, normalizeCityKey]);

  const fallbackCity = sortedCities[0]?.id || "";

  useEffect(() => {
    if (!cities.length) {
      refetchMeta?.();
    }
  }, [cities.length, refetchMeta]);
  useEffect(() => {
    if (!fallbackCity) {
      setFallbackDefaults({ leagueId: "", seasonId: "" });
      return;
    }

    let isActive = true;
    const run = async () => {
      try {
        const scopeRes = await api.get("/api/meta/default-scope", {
          params: { cityId: fallbackCity },
        });
        const { leagueId, seasonId } = scopeRes?.data || {};

        if (isActive) {
          setFallbackDefaults({
            leagueId: leagueId ? String(leagueId) : "",
            seasonId: seasonId ? String(seasonId) : "",
          });
        }
      } catch (error) {
        console.error(error);
        if (isActive) setFallbackDefaults({ leagueId: "", seasonId: "" });
      }
    };

    run();
    return () => {
      isActive = false;
    };
  }, [fallbackCity]);

  const fallbackHome =
    fallbackCity && fallbackDefaults.leagueId && fallbackDefaults.seasonId
      ? `/sehir/${encodeSegment(fallbackCity)}/lig/${encodeSegment(fallbackDefaults.leagueId)}/sezon/${encodeSegment(
          fallbackDefaults.seasonId
        )}/anasayfa`
      : fallbackCity
      ? `/sehir/${encodeSegment(fallbackCity)}`
      : "/";

  const fallbackMatches =
    fallbackCity && fallbackDefaults.leagueId && fallbackDefaults.seasonId
      ? `/sehir/${encodeSegment(fallbackCity)}/lig/${encodeSegment(fallbackDefaults.leagueId)}/sezon/${encodeSegment(
          fallbackDefaults.seasonId
        )}/maclar`
      : fallbackHome;

  const handleMapClick = useCallback(
    (event) => {
      const target = event.target.closest('.city');
      if (!target) return;
      const rawName = target.getAttribute('data-city-name') || target.getAttribute('title');
      const cityKey = normalizeCityKey(rawName);
      const cityId = cityIdMap.get(cityKey);
      if (!cityId) return;
      navigate(`/sehir/${encodeSegment(cityId)}`);
    },
    [cityIdMap, navigate, normalizeCityKey]
  );

  // Harita parçası geç yüklendiğinden aşağıdaki DOM etkileri SVG hazır olunca
  // (ve şehir verisi değişince) yeniden çalışır.
  const [mapReady, setMapReady] = useState(false);
  const markMapReady = useCallback(() => setMapReady(true), []);

  useEffect(() => {
    const svg = document.getElementById('svg-turkey-map');
    if (!svg) return;
    svg.querySelectorAll('.city').forEach((path) => {
      const citySlug = normalizeCityKey(path.getAttribute('data-city-name') || path.getAttribute('title'));
      if (citySlug && citiesWithData.has(citySlug)) {
        path.classList.add('has-data');
      } else {
        path.classList.remove('has-data');
      }
    });
  }, [citiesWithData, mapReady]);

  useEffect(() => {
    const tooltip = tooltipRef.current;
    const svg = document.getElementById('svg-turkey-map');
    if (!tooltip || !svg) return;

    const showTooltip = (event) => {
      const target = event.target.closest('.city');
      const label = target?.getAttribute('title') || target?.getAttribute('data-city-name');
      if (!label) {
        tooltip.classList.remove('is-visible');
        return;
      }
      tooltip.textContent = label;
      tooltip.style.left = `${event.clientX}px`;
      tooltip.style.top = `${event.clientY - 12}px`;
      tooltip.classList.add('is-visible');
    };

    const hideTooltip = () => tooltip.classList.remove('is-visible');

    svg.addEventListener('pointermove', showTooltip);
    svg.addEventListener('pointerleave', hideTooltip);

    return () => {
      svg.removeEventListener('pointermove', showTooltip);
      svg.removeEventListener('pointerleave', hideTooltip);
    };
  }, [mapReady]);

  return (
    <div className="hp-page hm">
      {/* Dekoratif zemin: sayfanın üst menüsü yok, marka ve içerik doğrudan
          burada başlar. Lavanta zemin, ince ızgara ve yumuşak mor ışıklar. */}
      <div className="hm__aurora hm__aurora--one" aria-hidden="true" />
      <div className="hm__aurora hm__aurora--two" aria-hidden="true" />
      <div className="hm__grid" aria-hidden="true" />

      <div className="hm__tooltip" ref={tooltipRef} />

      <div className="hm__inner">
        {/* ÜST BÖLÜM: solda tanıtım, sağda haritanın dışında duran giriş kartı */}
        <section className="hm__intro">
          <span className="hm__stripes" aria-hidden="true" />
          <div className="hm__lead">
            <img src={Logo} alt="ElitLig" className="hm__logo" />
            <span className="hm__pill"><i className="fa-solid fa-futbol" /> Türkiye'nin halı saha ligi</span>
            <h1>
              Şehrini seç, <span>ligine gir.</span>
            </h1>
            <p>
              ElitLig halı saha ligleri Türkiye genelinde oynanıyor. Haritadan şehrini
              seç; fikstür, puan durumu, oyuncu profilleri ve istatistikler anında karşına
              gelsin.
            </p>

            {sortedCities.length > 0 && (
              <ul className="hm__facts" aria-label="Lig özeti">
                <li><b>{sortedCities.length}</b><span>Aktif şehir</span></li>
                <li><i className="fa-solid fa-bolt" aria-hidden="true" /><span>Canlı skor</span></li>
              </ul>
            )}

            {fallbackCity && (
              <div className="hm__lead-actions">
                {/* Üye olmadan gezinmek isteyen ziyaretçinin ana çağrısı. */}
                <button type="button" className="hm__cta hm__cta--guest" onClick={() => navigate(fallbackHome)}>
                  <i className="fa-solid fa-user-astronaut" />
                  Misafir olarak giriş yap
                  <i className="fa-solid fa-arrow-right hm__cta-arrow" />
                </button>
                <Link className="hm__cta hm__cta--ghost" to={fallbackMatches}>
                  <i className="fa-solid fa-calendar-days" />
                  Maçları gör
                </Link>
              </div>
            )}
          </div>

          {/* Giriş kartı haritanın parçası değil; kendi kartında durur. */}
          <HomeLoginCard />
        </section>

        {/* ALT BÖLÜM: harita kendi kartında, tam genişlikte */}
        <section className="hm__map-card">
          <header className="hm__map-head">
            <div>
              <p className="hm__eyebrow">Şehir bazlı keşif</p>
              <h2>Haritadan şehrini seç</h2>
            </div>
            <div className="hm__legend">
              <span className="hm__legend-item">
                <i className="hm__dot hm__dot--active" /> Aktif lig var
              </span>
              <span className="hm__legend-item">
                <i className="hm__dot" /> Yakında
              </span>
            </div>
          </header>

          <div className="hm__canvas" onClick={handleMapClick}>
            <Suspense fallback={<div className="hm__map" aria-busy="true" />}>
              <TurkeyMap onReady={markMapReady} />
            </Suspense>
          </div>

          {sortedCities.length > 0 && (
            <div className="hm__cities">
              <span className="hm__cities-label">Aktif şehirler</span>
              <div className="hm__chips">
                {sortedCities.map((city) => (
                  <button
                    key={city.id || city.key}
                    type="button"
                    className="hm__chip"
                    onClick={() => city.id && navigate(`/sehir/${encodeSegment(city.id)}`)}
                  >
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    {city.label}
                    <i className="fa-solid fa-arrow-right hm__chip-go" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
